import React from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './styled-components.d.ts';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Contact from './components/Contact';
import Footer from './components/Footer';
import TermsOfUse from './components/TermsOfUse';
import AccessibilityStatement from './components/AccessibilityStatement';
import ScrollToTop from './components/ScrollToTop';
import CookieConsent from './components/CookieConsent';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    direction: rtl;
    font-family: 'Heebo', 'Segoe UI', 'Arial', sans-serif;
    scroll-behavior: smooth;
  }

  body {
    line-height: 1.7;
    color: #4a5568;
    background: linear-gradient(135deg, #fffaf0 0%, #fef5e7 100%);
    min-height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
  }

  /* Accessibility: Focus indicators for keyboard navigation */
  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 3px solid #d69e2e;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(214, 158, 46, 0.3);
  }

  /* Skip to content link for screen readers */
  .skip-to-content {
    position: absolute;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    background: #d69e2e;
    color: white;
    padding: 1rem 2rem;
    text-decoration: none;
    border-radius: 0 0 8px 8px;
    font-weight: 600;
    z-index: 10000;
    transition: top 0.3s;
  }

  .skip-to-content:focus {
    top: 0;
  }

  /* Ensure sufficient color contrast for links */
  a:hover {
    text-decoration: underline;
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: currentColor !important;
    }
  }

  /* Reduced motion for users who prefer it */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

const theme = {
  colors: {
    primary: '#d69e2e',
    secondary: '#f6e05e',
    accent: '#fbb6ce',
    warm: '#fed7d7',
    text: '#4a5568',
    textLight: '#a0aec0',
    textWarm: '#744210',
    background: '#fffaf0',
    backgroundLight: '#fef5e7',
    backgroundSoft: '#f7fafc',
    border: '#fbd38d',
    borderLight: '#fed7aa'
  },
  fonts: {
    main: "'Heebo', 'Comic Neue', 'Segoe UI', sans-serif",
    heading: "'Heebo', 'Dancing Script', 'Georgia', serif",
    accent: "'Dancing Script', 'Amatic SC', cursive"
  },
  shadows: {
    light: '0 2px 8px rgba(214, 158, 46, 0.1)',
    medium: '0 4px 12px rgba(214, 158, 46, 0.15)',
    heavy: '0 8px 20px rgba(214, 158, 46, 0.2)',
    warm: '0 4px 15px rgba(251, 182, 206, 0.3)'
  }
};

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
`;

const HomePage = () => (
  <>
    <Hero />
    <About />
    <Services />
    <Contact />
  </>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <ScrollToTop />
        <a href="#main-content" className="skip-to-content">
          דלג לתוכן הראשי
        </a>
        <AppContainer>
          <Header />
          <MainContent id="main-content" role="main" aria-label="תוכן ראשי">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/accessibility" element={<AccessibilityStatement />} />
            </Routes>
          </MainContent>
          <Footer />
          <CookieConsent />
        </AppContainer>
      </Router>
    </ThemeProvider>
  );
}

export default App;
